import Wrapper from "components/Card/Wrapper";
import {
  Grid,
  TextField,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import langString from "utils/langString";
import ReactApexChart from "react-apexcharts";
import axiosServices from "utils/axiosServices";

const HospitalResponseTime = () => {
  const [response, setResponse] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filter states
  const [hospitalType, setHospitalType] = useState<string>("");
  const [ageStart, setAgeStart] = useState<string>("");
  const [ageEnd, setAgeEnd] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [injuryType, setInjuryType] = useState<string>("");
  const [toDateRange, setToDateRange] = useState<any>();
  const [fromDateRange, setFromDateRange] = useState<any>();
  const [severity, setSeverity] = useState<any[]>([]);
  // Options for filters
  const hospitalTypes = [
    "Government Hospital",
    "Upazila Hospital",
    "Private Hospital",
    "Nitor",
  ];

  const genderOptions = ["Male", "Female", "Other"];

  useEffect(() => {
    const postData = { type: "severity" };
    const init = async () => {
      setLoading(true);
      try {
        const response = await axiosServices.post(`data/get-all`, postData);
        if (response.status === 200) {
          setSeverity(response.data.dataList);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    };
    init();
  }, []);

  const handleFilter = async () => {
    const body = {
      hospitalType: hospitalType || undefined,
      ageStart: ageStart || undefined,
      ageEnd: ageEnd || undefined,
      gender: gender || undefined,
      injuryType: injuryType || undefined,
      startDate: fromDateRange,
      endDate: toDateRange,
    };
    console.log("Filter Body:", body);
    try {
      const response = await axiosServices.post(
        `report/responseHospitalReport`,
        body
      );
      if (response.status === 200) {
        setResponse(response.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleReset = () => {
    setHospitalType("");
    setAgeStart("");
    setAgeEnd("");
    setGender("");
    setInjuryType("");
    setFromDateRange("");
    setToDateRange("");
    init();
  };

  const init = async () => {
    setLoading(true);
    try {
      const response = await axiosServices.post(
        `report/responseHospitalReport`
      );
      if (response.status === 200) {
        setResponse(response.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    init();
  }, []);

  // Parse data for chart - expects array of {hospitalName, totalIncidents}
  const parseHospitalData = () => {
    if (!response || !Array.isArray(response)) {
      return { categories: [], data: [] };
    }

    const categories = response.map(
      (hospital: any) => hospital.hospitalName || "Unknown"
    );
    const data = response.map((hospital: any) => hospital.totalIncidents || 0);

    return { categories, data };
  };

  const { categories, data } = parseHospitalData();

  const options: any = {
    chart: {
      height: 600,
      type: "bar",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        horizontal: true,
        dataLabels: {
          position: "right",
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        colors: ["#304758"],
      },
    },
    title: {
      text: "Hospital Response - Total Incidents",
      style: {
        fontSize: "18px",
        fontWeight: "bold",
      },
    },
    xaxis: {
      categories: categories.length > 0 ? categories : ["No Data"],
      title: {
        text: "Number of Incidents",
      },
    },
    // yaxis: {
    //   title: {
    //     text: "Hospitals",
    //   },
    // },
    colors: ["#1976d2"],
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val + " incidents";
        },
      },
    },
  };

  const series: any = [
    {
      name: "Total Incidents",
      data: data.length > 0 ? data : [0],
    },
  ];
  console.log("severity:", severity);
  return (
    <Wrapper title={`${langString("Hospital Response Times")} `}>
      <Grid container alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Report Filter By Criteria
          </Typography>
        </Grid>

        {/* Hospital Type */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Hospital Type</InputLabel>
            <Select
              value={hospitalType}
              label="Hospital Type"
              onChange={(e) => setHospitalType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {hospitalTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Age From */}
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Age From"
            type="number"
            value={ageStart}
            onChange={(e) => setAgeStart(e.target.value)}
            inputProps={{ min: 0, max: 120 }}
          />
        </Grid>

        {/* Age To */}
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Age To"
            type="number"
            value={ageEnd}
            onChange={(e) => setAgeEnd(e.target.value)}
            inputProps={{ min: 0, max: 120 }}
          />
        </Grid>

        {/* Gender */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Gender</InputLabel>
            <Select
              value={gender}
              label="Gender"
              onChange={(e) => setGender(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {genderOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Injury Type */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Injury Type</InputLabel>
            <Select
              value={injuryType}
              label="Injury Type"
              onChange={(e) => setInjuryType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {severity.map((type) => (
                <MenuItem key={type?.id} value={type?.value}>
                  {type?.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* From Date */}
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            label="From Date"
            type="date"
            value={fromDateRange}
            onChange={(e) => setFromDateRange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* To Date */}
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            label="To Date"
            type="date"
            value={toDateRange}
            onChange={(e) => setToDateRange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Filter Button */}
        <Grid item xs={6} sm={3} md={1}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleFilter}
            sx={{ height: "40px" }}
          >
            Filter
          </Button>
        </Grid>

        {/* Reset Button */}
        <Grid item xs={6} sm={3} md={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleReset}
            sx={{ height: "40px" }}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <div id="chart">
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={600}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default HospitalResponseTime;
